import axios from "axios";
import env from "../variables/config";

axios.defaults.baseURL = env.apiUrl;

const config = () => ({
  headers: { Authorization: "Bearer " + localStorage.getItem("token") }
});

export const getEvents = async (params?: { 
  page?: number; 
  limit?: number; 
  search?: string; 
  stage?: string; 
  status?: string;
  startDate?: string;
  endDate?: string;
  signalCreated?: string;
  minScore?: string;
  maxScore?: string;
  enrichmentStatus?: string;
  source?: string;
  minCompanies?: string;
  maxCompanies?: string;
  eventType?: string;
  hasNotes?: string;
  city?: string;
  sortBy?: string;
  sortOrder?: string;
  enrichmentFilters?: string;
}) => {
  const queryString = new URLSearchParams(params as any).toString();
  const res = await axios.get(`/admin/events${queryString ? `?${queryString}` : ""}`, config());
  return res.data;
};

export const getEventStats = async () => {
  const res = await axios.get("/admin/events/stats", config());
  return res.data;
};

export const getWeeklyEventStats = async (weeks: number = 12, eventType?: string) => {
  const params = new URLSearchParams({ weeks: weeks.toString() });
  if (eventType) {
    params.append('eventType', eventType);
  }
  const res = await axios.get(`/admin/events/stats/weekly?${params.toString()}`, config());
  return res.data;
};

export const getMonthlyEventStats = async (months: number = 12, eventType?: string) => {
  const params = new URLSearchParams({ months: months.toString() });
  if (eventType) {
    params.append('eventType', eventType);
  }
  const res = await axios.get(`/admin/events/stats/monthly?${params.toString()}`, config());
  return res.data;
};

export const getEvent = async (id: string) => {
  const res = await axios.get(`/admin/events/${id}`, config());
  return res.data;
};

export const updateEvent = async (id: string, data: any) => {
  const res = await axios.put(`/admin/events/${id}`, data, config());
  return res.data;
};

export const triggerEnrichment = async (id: string, enrichmentType: string) => {
  const res = await axios.post(`/admin/events/${id}/enrich`, { enrichmentType }, config());
  return res.data;
};

export const updatePerson = async (eventId: string, personId: string, data: any) => {
  const res = await axios.put(`/admin/events/${eventId}/people/${personId}`, data, config());
  return res.data;
};

export const selectEnrichmentOption = async (eventId: string, enrichmentType: string, option: any) => {
  const res = await axios.post(`/admin/events/${eventId}/enrichments/${enrichmentType}/select`, { option }, config());
  return res.data;
};

export const selectPersonEnrichmentOption = async (eventId: string, personId: string, enrichmentType: string, option: any) => {
  const res = await axios.post(`/admin/events/${eventId}/people/${personId}/enrichments/${enrichmentType}/select`, { option }, config());
  return res.data;
};

export const deleteEvent = async (id: string) => {
  const res = await axios.delete(`/admin/events/${id}`, config());
  return res.data;
};

export const createCompanyAndSignalsFromEvent = async (eventId: string) => {
  const res = await axios.post(`/admin/events/${eventId}/create-company-signals`, {}, config());
  return res.data;
};

export const calculateEventScore = async (eventId: string) => {
  const res = await axios.post(`/admin/events/${eventId}/calculate-score`, {}, config());
  return res.data;
};

export interface EnrichmentSuggestion {
  eventId: string;
  companyName: string;
  source: string;
  sourceUrl: string;
  eventType?: string;
  detectedAt?: string;
  siren?: string;
  address?: string;
  restaurantName?: string | null;
  enrichmentType: string;
  enrichmentStatus: string;
  aiConfidenceLevel?: "unverified" | "low" | "medium" | "high";
  data?: any;
  options: any[];
  lastRunAt?: string;
  target: "event" | "person";
  personId?: string | null;
  personName?: string | null;
  personRole?: string | null;
  googlePlace?: { type: string; data: any } | null;
}

export const getImportBatches = async (): Promise<{ batches: string[] }> => {
  const res = await axios.get("/admin/events/import-batches", config());
  return res.data;
};

export const getEnrichmentSuggestions = async (params?: {
  page?: number;
  limit?: number;
  type?: string;
  eventType?: string;
  withPeople?: boolean;
  period?: string;
  city?: string;
  from?: string;
  to?: string;
  importBatch?: string;
  confidenceFilter?: string;
  minLinkedinScore?: number;
}) => {
  const queryString = new URLSearchParams(params as any).toString();
  const res = await axios.get(`/admin/events/enrichment-suggestions${queryString ? `?${queryString}` : ""}`, config());
  return res.data;
};

export const approveEnrichmentSuggestion = async (eventId: string, data: { enrichmentType: string; selectedOption: any; personId?: string }) => {
  const res = await axios.post(`/admin/events/enrichment-suggestions/${eventId}/approve`, data, config());
  return res.data;
};

export const rejectEnrichmentSuggestion = async (eventId: string, data: { enrichmentType: string; personId?: string }) => {
  const res = await axios.post(`/admin/events/enrichment-suggestions/${eventId}/reject`, data, config());
  return res.data;
};

export const purgeEnrichmentSuggestion = async (eventId: string, data: { enrichmentType: string; personId?: string }) => {
  const res = await axios.post(`/admin/events/enrichment-suggestions/${eventId}/purge`, data, config());
  return res.data;
};

export interface EnrichmentValidationStats {
  accepted: number;
  rejected: number;
  retry: number;
  pending: number;
}

export const getEnrichmentValidationStats = async (params?: { type?: string; from?: string; to?: string }) => {
  const queryString = params ? new URLSearchParams(params as any).toString() : '';
  const res = await axios.get(`/admin/events/enrichment-suggestions/stats${queryString ? `?${queryString}` : ""}`, config());
  return res.data;
};

export interface EnrichmentTypeStats {
  pending: number;   // not enriched yet
  done: number;      // enriched, waiting for approval
  approved: number;
  rejected: number;
  retry: number;
}

export const getEnrichmentStatsByType = async (params?: { from?: string; to?: string; eventType?: string }) => {
  const queryString = params ? new URLSearchParams(params as any).toString() : '';
  const res = await axios.get(`/admin/events/enrichment-suggestions/stats-by-type${queryString ? `?${queryString}` : ""}`, config());
  return res.data as { byType: Record<string, EnrichmentTypeStats> };
};

export interface ReviewedEnrichment {
  entityType: "event" | "person";
  entityId: string;
  companyName?: string;
  source?: string;
  personName?: string;
  personRole?: string;
  establishmentId?: string;
  /** The approved payload was deleted by the old boot cleanup: `data` is the establishment's current value. */
  dataFromEstablishment?: boolean;
  enrichment: {
    type: string;
    status: "approved" | "rejected";
    data?: any;
    options?: any[];
    reviewedAt: string;
    isManuallyReviewed: boolean;
    reviewedBy?: string;
    /** Set when reviewedAt was rebuilt after the fact (activity log, establishment…). */
    reviewedAtRecoveredFrom?: string;
    reviewedByUser?: {
      _id: string;
      email?: string;
      name?: string;
    };
  };
}

export const getReviewedEnrichments = async (params?: {
  page?: number;
  limit?: number;
  status?: "approved" | "rejected";
  isManuallyReviewed?: "true" | "false";
  enrichmentType?: string;
  entityType?: "event" | "person";
  sortOrder?: "asc" | "desc";
  startDate?: string;
  endDate?: string;
}) => {
  const queryString = new URLSearchParams(params as any).toString();
  const res = await axios.get(`/admin/events/enrichments/reviewed${queryString ? `?${queryString}` : ""}`, config());
  return res.data;
};

export const getRestaurantDatabaseEvents = async (params?: { 
  page?: number; 
  limit?: number; 
  search?: string;
  minReviews?: string;
  location?: string;
  minRating?: string;
  detectionDate?: string;
  address?: string;
  establishmentNumber?: string;
  contactFilters?: string;
  networkStatus?: string;
  establishmentType?: string;
  concept?: string;
}) => {
  const queryString = new URLSearchParams(params as any).toString();
  const res = await axios.get(`/restaurant-database${queryString ? `?${queryString}` : ""}`, config());
  return res.data;
};

export interface Person {
  _id: string;
  name: string;
  role: "primary" | "secondary";
  relevanceScore: number;
  enrichments: Array<{
    type: string;
    status: "pending" | "done" | "error";
    data?: any;
    options?: any[];
    lastRunAt?: string;
    error?: string;
  }>;
  createdAt?: string;
  updatedAt?: string;
}

export interface Event {
  enrichmentProgressDone?: number;
  enrichmentProgressTotal?: number;
  _id: string;
  eventType?: string;
  source: string;
  sourceUrl: string;
  companyName: string;
  description?: string;
  detectedAt?: string;
  uniqueHash: string;
  enrichments: Array<{
    type: string;
    status: "pending" | "done" | "error";
    data?: any;
    options?: any[];
    lastRunAt?: string;
    error?: string;
  }>;
  people: Person[];
  processing: {
    stage: string;
    percentPeopleEnriched?: number;
    lastUpdate: string;
  };
  score?: {
    total: number;
    addressMatch?: {
      similarity: number;
      quality: "not_matching" | "close" | "really_close";
    };
    nameMatch?: {
      similarity: number;
      quality: "not_matching" | "close" | "really_close";
    };
    displayNameIssue?: boolean;
    lastCalculatedAt?: string;
  };
  signalCreated?: boolean;
  isManuallyVerified?: boolean;
  notes?: string;
  staffRating?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface EnrichmentWeeklyData {
  label: string;
  googlePlaceDone: number;
  googlePlacePending: number;
  googlePlaceDFSDone: number;
  googlePlaceDFSPending: number;
  linkedinPending: number;
  foundersAdded: number;
  birthdateCompleted: number;
  birthdatePending: number;
  noPeopleEvents: number;
  noWebsiteEvents: number;
  noEmailEvents: number;
  googlePlaceDFSStatusPending: number;
  googlePlaceDFSStatusDone: number;
  googlePlaceDFSStatusWaitingCallback: number;
  googlePlaceDFSStatusApproved: number;
  googlePlaceDFSStatusError: number;
  googlePlaceDFSStatusRetry: number;
  emailDone: number;
  emailPendingNoWebsite: number;
  emailPendingWithWebsite: number;
  aiConfidenceLevelHigh: number;
  aiConfidenceLevelMedium: number;
  aiConfidenceLevelLow: number;
  aiConfidenceLevelUnverified: number;
}

export interface EnrichmentMonthlyDFSData {
  label: string;
  googlePlaceDFSStatusPending: number;
  googlePlaceDFSStatusDone: number;
  googlePlaceDFSStatusWaitingCallback: number;
  googlePlaceDFSStatusApproved: number;
  googlePlaceDFSStatusError: number;
  googlePlaceDFSStatusRetry: number;
  emailDone: number;
  emailPendingNoWebsite: number;
  emailPendingWithWebsite: number;
  aiConfidenceLevelHigh: number;
  aiConfidenceLevelMedium: number;
  aiConfidenceLevelLow: number;
  aiConfidenceLevelUnverified: number;
}

export interface AllTimeDFSStats {
  pending: number;
  done: number;
  waitingCallback: number;
  approved: number;
  exactMatchApproved: number;
  error: number;
  retry: number;
}

export interface AllTimeEmailStats {
  done: number;
  pendingNoWebsite: number;
  pendingWithWebsite: number;
}

export interface AllTimeConfidenceStats {
  high: number;
  medium: number;
  low: number;
  unverified: number;
}

export interface EnrichmentStatusStats {
  weeklyData: EnrichmentWeeklyData[];
  monthlyDFSData: EnrichmentMonthlyDFSData[];
  allTimeDFS: AllTimeDFSStats;
  allTimeEmail: AllTimeEmailStats;
  allTimeConfidence: AllTimeConfidenceStats;
}

export const getEnrichmentStatusStats = async (dfsDateField?: "createdAt" | "detectedAt"): Promise<EnrichmentStatusStats> => {
  const qs = dfsDateField ? `?dfsDateField=${dfsDateField}` : "";
  const res = await axios.get(`/admin/events/admin/enrichment-stats${qs}`, config());
  return res.data;
};

export interface EmailEnrichmentTodoItem {
  eventId: string;
  companyName: string;
  source: string;
  sourceUrl?: string;
  eventType?: string;
  detectedAt?: string;
  emailData?: any;
  emailOptions?: any[];
  lastRunAt?: string;
  websiteUrl?: string | null;
  websiteStatus?: string | null;
  people?: { id: string; name: string }[];
  ownerDetected?: boolean;
}

export const getEmailEnrichmentTodos = async (params?: { page?: number; limit?: number; from?: string; to?: string; minScore?: number }) => {
  const queryString = params ? new URLSearchParams(params as any).toString() : "";
  const res = await axios.get(`/admin/events/email-enrichments${queryString ? `?${queryString}` : ""}`, config());
  return res.data;
};

export const approveEmailEnrichment = async (
  eventId: string,
  data: { businessOption?: any; ownerOption?: any; ownerPersonId?: string }
) => {
  const res = await axios.post(`/admin/events/email-enrichments/${eventId}/approve`, data, config());
  return res.data;
};

export const rejectEmailEnrichment = async (eventId: string) => {
  const res = await axios.post(`/admin/events/email-enrichments/${eventId}/reject`, {}, config());
  return res.data;
};

// ─── Franchise review ────────────────────────────────────────────────────────

export interface FranchiseReviewEstablishment {
  _id: string;
  name: string;
  legal_name: string | null;
  city: string | null;
  postcode: string | null;
  isFranchise: boolean;
  franchise: { _id: string; name: string } | null;
}

export type FranchiseReviewBucket = "pending" | "unsure" | "approved" | "rejected";

export interface FranchiseReviewItem {
  eventId: string;
  companyName: string;
  detectedAt: string;
  franchiseName: string | null;
  status: string | null;
  tentativeFranchise: { _id: string; name: string } | null;
  siren: string | null;
  establishment: FranchiseReviewEstablishment | null;
}

export interface FranchiseReviewResponse {
  data: FranchiseReviewItem[];
  total: number;
  page: number;
  totalPages: number;
  bucket: FranchiseReviewBucket;
}

export interface FranchiseReviewCounts {
  pending: number;
  unsure: number;
  approved: number;
  rejected: number;
}

export const getFranchiseReviewEvents = async (params?: {
  page?: number;
  limit?: number;
  hasFranchise?: "none" | "has" | "all";
  status?: FranchiseReviewBucket;
}): Promise<FranchiseReviewResponse> => {
  const qs = params ? new URLSearchParams(params as any).toString() : "";
  const res = await axios.get(`/admin/events/franchise-review${qs ? `?${qs}` : ""}`, config());
  return res.data;
};

export const getFranchiseReviewCounts = async (): Promise<FranchiseReviewCounts> => {
  const res = await axios.get(`/admin/events/franchise-review/count`, config());
  return res.data;
};

export const approveFranchiseReview = async (eventId: string, franchiseId: string): Promise<void> => {
  await axios.post(`/admin/events/${eventId}/franchise-review/approve`, { franchiseId }, config());
};

export const markFranchiseReviewUnsure = async (eventId: string, franchiseId?: string): Promise<void> => {
  await axios.post(`/admin/events/${eventId}/franchise-review/unsure`, franchiseId ? { franchiseId } : {}, config());
};

export const reopenFranchiseReview = async (eventId: string): Promise<void> => {
  await axios.post(`/admin/events/${eventId}/franchise-review/reopen`, {}, config());
};

export const rejectFranchiseReview = async (eventId: string): Promise<void> => {
  await axios.post(`/admin/events/${eventId}/franchise-review/reject`, {}, config());
};
